import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import Avatar from 'components/@extended/Avatar';
import { PopupTransition } from 'components/@extended/Transitions';
import { Button, Dialog, DialogContent, Divider, Stack, Typography } from '@mui/material';

export const ConfirmModalType = {
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error'
};

const typeConfig = {
    [ConfirmModalType.SUCCESS]: { icon: <CheckCircleOutlined />, color: 'success' },
    [ConfirmModalType.WARNING]: { icon: <ExclamationCircleOutlined />, color: 'warning' },
    [ConfirmModalType.ERROR]: { icon: <CloseCircleOutlined />, color: 'error' }
};

/**
 * ConfirmFormModal
 * A configurable confirm modal that can render a custom form/body inside.
 *
 * Features:
 * - children or renderBody for fully custom content
 * - optional validation (sync/async) via onValidate
 * - optional value extraction via getValues
 * - async onConfirm support with loading + error handling
 */
const ConfirmFormModal = ({
    title,
    text,
    open,
    onClose,
    onConfirm,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = ConfirmModalType.WARNING,
    children,
    renderBody,
    onValidate,
    getValues,
    closeOnConfirm = true,
    disableConfirm = false,
    maxWidth = 'xs',
    loadingText = 'Saving...'
}) => {
    const { icon, color } = typeConfig[type] || typeConfig[ConfirmModalType.WARNING];
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const body = useMemo(() => {
        if (typeof renderBody === 'function') return renderBody({ submitting, setError });
        return children;
    }, [renderBody, children, submitting]);

    const handleConfirm = async () => {
        setError(null);

        if (onValidate) {
            const validationResult = await onValidate();
            if (validationResult !== true) {
                setError(typeof validationResult === 'string' ? validationResult : null);
                return;
            }
        }

        const values = getValues ? getValues() : undefined;

        try {
            setSubmitting(true);
            await onConfirm(values);

            if (closeOnConfirm) onClose();
        } catch (e) {
            setError(e?.message || 'Something went wrong.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            keepMounted
            TransitionComponent={PopupTransition}
            maxWidth={maxWidth}
            fullWidth
            aria-labelledby="confirm-form-modal-title"
            aria-describedby="confirm-form-modal-description"
        >
            <DialogContent sx={{ mt: 2, my: 1 }}>
                <Stack alignItems="center" spacing={3.5}>
                    <Avatar
                        color={color}
                        sx={{ width: 72, height: 72, fontSize: '1.75rem' }}
                    >
                        {icon}
                    </Avatar>

                    <Stack spacing={1.5} sx={{ width: 1 }}>
                        <Typography
                            id="confirm-form-modal-title"
                            variant="h4"
                            align="center"
                        >
                            {title}
                        </Typography>

                        {text ? (
                            <Typography
                                id="confirm-form-modal-description"
                                align="center"
                            >
                                {text}
                            </Typography>
                        ) : null}

                        {children || renderBody ? (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <Stack spacing={2} sx={{ width: 1 }}>
                                    {body}
                                </Stack>
                            </>
                        ) : null}

                        {error ? (
                            <Typography color="error" align="center" sx={{ mt: 1 }}>
                                {error}
                            </Typography>
                        ) : null}
                    </Stack>

                    <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                        <Button
                            fullWidth
                            color="secondary"
                            variant="outlined"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            {cancelText}
                        </Button>

                        <Button
                            fullWidth
                            color={color}
                            variant="contained"
                            onClick={handleConfirm}
                            autoFocus
                            disabled={disableConfirm || submitting}
                        >
                            {submitting ? loadingText : confirmText}
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

ConfirmFormModal.propTypes = {
    title: PropTypes.string,
    text: PropTypes.node,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,

    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    type: PropTypes.oneOf(Object.values(ConfirmModalType)),

    children: PropTypes.node,
    renderBody: PropTypes.func,

    onValidate: PropTypes.func,
    getValues: PropTypes.func,

    closeOnConfirm: PropTypes.bool,
    disableConfirm: PropTypes.bool,
    maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    loadingText: PropTypes.string
};

export default ConfirmFormModal;
